import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FiPhone, FiMail, FiMapPin, FiInstagram, FiFacebook, FiTwitter } from 'react-icons/fi';
import PageModal from './PageModal';
import { getApiUrl } from '../utils/api';

const Footer = ({ onOpenModal }) => {
  const currentYear = new Date().getFullYear();
  const [pageModal, setPageModal] = useState({ isOpen: false, slug: null });
  const [footerPages, setFooterPages] = useState([]);

  useEffect(() => {
    fetchFooterPages();
  }, []);

  const fetchFooterPages = async () => {
    try {
      const response = await fetch(getApiUrl('pages/menu/footer'));
      if (response.ok) {
        const data = await response.json();
        setFooterPages(data.data || { services: [], company: [], support: [] });
      }
    } catch (error) {
      console.error('Error fetching footer pages:', error);
      setFooterPages({ services: [], company: [], support: [] });
    }
  };

  const openPageModal = (slug) => {
    setPageModal({ isOpen: true, slug });
  };

  const closePageModal = () => {
    setPageModal({ isOpen: false, slug: null });
  };

  const footerLinks = {
    services: [
      { name: 'Floral Designs', href: '#products' },
      { name: 'Bridal Stage & Table', href: '#products' },
      { name: 'Lighting & Drapes', href: '#products' },
      { name: 'Furniture & Props', href: '#products' },
      ...(footerPages.services || []).map(page => ({
        name: page.title,
        isPage: true,
        slug: page.slug
      }))
    ],
    company: [
      { name: 'About Us', href: '#about' },
      { name: 'Contact', href: '#contact' },
      ...(footerPages.company || []).map(page => ({
        name: page.title,
        isPage: true,
        slug: page.slug
      }))
    ],
    support: [
      { name: 'FAQs', href: '#faqs' },
      ...(footerPages.support || []).map(page => ({
        name: page.title,
        isPage: true,
        slug: page.slug
      }))
    ]
  };

  const socialLinks = [
    { icon: FiInstagram, href: 'https://instagram.com/melbournefloralhire', label: 'Instagram' },
    { icon: FiFacebook, href: 'https://facebook.com/melbournefloralhire', label: 'Facebook' },
    { icon: FiTwitter, href: 'https://twitter.com/melbournefloralhire', label: 'Twitter' }
  ];

  return (
    <footer className="bg-[#475908] text-white py-4">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 pt-16 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[40%_20%_20%_20%] gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <Image
                src="/images/logo_white.png"
                alt="Melbourne Floral Hire Logo"
                width={230}
                height={100}
                className="mb-2"
              />
              <p className="text-neutral-300 text-sm">
                Premium faux floral arrangements for hire in Melbourne.
                Creating beautiful memories for your special occasions.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <FiPhone size={16} className="text-primary-300" />
                <span className="text-neutral-300">0466 645 060</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <FiMail size={16} className="text-primary-300" />
                <span className="text-neutral-300">hire@melbournefloralhire.com.au</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <FiMapPin size={16} className="text-primary-300" />
                <span className="text-neutral-300">Warehouse 10, 30 Tower Court, Noble Park VIC 3174</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 pb-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-neutral-700 hover:bg-secondary-500 rounded-full flex items-center justify-center transition-colors"
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-white mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-neutral-300 hover:text-primary-300 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-neutral-300 hover:text-primary-300 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.isPage ? (
                    <button
                      onClick={() => openPageModal(link.slug)}
                      className="text-neutral-300 hover:text-primary-300 text-sm transition-colors text-left"
                    >
                      {link.name}
                    </button>
                  ) : link.name === 'Privacy Policy' ? (
                    <button
                      onClick={() => onOpenModal('privacy')}
                      className="text-neutral-300 hover:text-primary-300 text-sm transition-colors text-left"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="text-neutral-300 hover:text-primary-300 text-sm transition-colors"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-neutral-300 text-sm">
              © {currentYear} Melbourne Floral Hire. All rights reserved.
            </div>

            <div className="flex items-center gap-6">
              <div className="text-neutral-300 text-sm">
                Designed with <span className="text-primary-300">♥</span> in Melbourne
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Modal */}
      <PageModal
        isOpen={pageModal.isOpen}
        onClose={closePageModal}
        slug={pageModal.slug}
      />
    </footer>
  );
};

export default Footer;